import { calculateDeadline } from "../../utils/time";
import type { UserRequest } from "./withdraw";

/**
 * Prepares a withdrawal request with the specified parameters
 * @param redeemAmount The amount of tokens to be redeemed
 * @param atomicPrice The current atomic price for the withdrawal
 * @param deadline Optional timestamp for when the request expires
 * @returns UserRequest object with withdrawal parameters
 */
const prepareUserRequest = (
  offerAmount: bigint,
  atomicPrice: bigint,
  deadline: number
): UserRequest => {
  const deadlineTimeStamp = calculateDeadline(deadline);
  return {
    deadline: deadlineTimeStamp,
    atomicPrice: atomicPrice,
    offerAmount: offerAmount,
    inSolve: false,
  };
};

/**
 * Calculates the final redeem amount accounting for exchange rate and slippage
 * @param amount The initial amount to be redeemed
 * @param exchangeRate The current exchange rate between tokens
 * @param slippage The slippage tolerance as a decimal (e.g., 0.01 for 1%)
 * @returns The final redeem amount after applying exchange rate and slippage
 */
// ! Deprecated - marked for removal
// const calculateWantAssetReceiveAmount = (
//   amount: bigint,
//   amountDecimals: number,
//   exchangeRate: bigint,
//   exchangeRateDecimals: number,
//   slippage: number
// ): bigint => {
//   if (exchangeRate === BigInt(0)) {
//     return BigInt(0);
//   }
//   if (amountDecimals === exchangeRateDecimals) {
//     // all good
//   }

//   if (amountDecimals > exchangeRateDecimals) {
//     // normalize to larger decimals
//   }
//   // Convert amount to the target token using exchange rate
//   // Multiply by WAD (10^18) for precision and divide by exchange rate
//   const baseAmount = (amount * WAD.bigint) / exchangeRate;

//   // Convert slippage percentage to WAD format for calculation
//   const slippageAsBigInt = BigInt((slippage * WAD.number).toString());

//   // Calculate the amount to be subtracted due to slippage
//   const slippageAmount = (baseAmount * slippageAsBigInt) / WAD.bigint;

//   // Return the final amount after subtracting slippage
//   return baseAmount - slippageAmount;
// };

/**
 *
 * @param rateInQuote in want asset decimals
 * @param wantAssetDecimals in want asset decimals
 * @param slippage in bps (e.g., 20 for 0.2%) 20 in 4 decimals = 20 / 10000  = 0.002  = 0.2%
 * @returns atomic price in want asset decimals
 */
const calculateAtomicPrice = (
  rateInQuote: bigint, // e.g., 1200000 (1.2 USDC) or 120000000 (1.2 WBTC)
  wantAssetDecimals: number, // 6 for USDC, 8 for WBTC - the "want" asset decimals
  slippage: number // In bps (e.g., 20 for 0.2%)
): bigint => {
  // Step 1: Create precision multiplier for want asset
  const wantPrecision = BigInt(10 ** wantAssetDecimals);

  // Step 2: Convert bps to decimal for calculation (bps / 10000)
  const slippageAsBigInt = (BigInt(slippage) * wantPrecision) / BigInt(10000);

  // Step 3: Apply formula: atomicPrice = rateInQuote * (1 - slippage)
  return (rateInQuote * (wantPrecision - slippageAsBigInt)) / wantPrecision;
};

export { calculateAtomicPrice, prepareUserRequest };
